package com.hyperether.planner.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Constraints
import com.hyperether.planner.model.Event
import com.hyperether.planner.model.Plan
import com.hyperether.planner.util.CircleWithInitial
import com.hyperether.planner.util.Dp
import com.hyperether.planner.util.Sp
import com.hyperether.planner.util.horizontalScrollbar
import com.hyperether.planner.util.verticalScrollbar
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

@Composable
fun SchedulerDayPlanner(
    listOfPlans: MutableList<Plan>,
    celWidth: Int = 50,
    startHour: Int = 8,
    borderPathColor: Color = Color(231, 231, 231),
    borderColor: Color = Color(239, 239, 239),
    namesColor: Color = Color(146, 144, 144),
    hoursColor: Color = Color(83, 82, 82),
    namesIconsShown: Boolean = false,
    onEventClicked: (Event) -> Unit,
    maxWidth: Int? = null,
    screenWidth: Float = 0f,
    screenHeight: Float = 0f,
    isMobile: Boolean
) {
    val cellHeight = Dp(50.0f)
    val cellSpacingTop = Dp(12.0f)
    val cellSpacing = Dp(32.0f)
    val eventCount = listOfPlans.sumOf { it.events.size }
    val calculatedHeight = (cellHeight.value + cellSpacing.value) * eventCount
    println("Scheduler recompose eventCount: $eventCount calculatedHeight: $calculatedHeight screenHeight:$screenHeight")
    val expandedNames = remember { mutableStateOf(!isMobile) }
    val celWidthDp = remember { mutableStateOf(celWidth) }
    val tableWidth = remember { mutableStateOf(Dp((24 - startHour) * celWidthDp.value.toFloat())) }
    val hourRange = 24 - startHour + 1
    maxWidth?.let {
        celWidthDp.value = remember(maxWidth) {
            val firstColumnWidth =
                if (namesIconsShown || expandedNames.value) (Dp(150f)) else Dp(50f)
            ((maxWidth - firstColumnWidth.value.toInt()) / hourRange).coerceAtLeast(40)
        }
        tableWidth.value = Dp(celWidthDp.value.toFloat() * (24 - startHour))
    } ?: run {
        celWidthDp.value = celWidth
        tableWidth.value = Dp(((24f - startHour) * celWidthDp.value))
    }

    if (listOfPlans.size < 10) {
        while (listOfPlans.size < 11)
            listOfPlans.addAll(listOf(Plan("", mutableListOf())))
    }

    val horizontalScrollState = rememberScrollState()
    val verticalScrollState = rememberScrollState()

    Box(
        modifier = Modifier
            .let { modifier ->
                if (calculatedHeight > screenHeight)
                    modifier.verticalScrollbar(verticalScrollState)
                else modifier
            }
            .horizontalScrollbar(horizontalScrollState)
            .fillMaxWidth()
            .pointerInput(Unit) {
                detectDragGestures { change, dragAmount ->
                    change.consume()
                    CoroutineScope(Dispatchers.Default).launch {
                        println("Y drag = ${dragAmount.y.toInt()}")
                        println("Y state = ${verticalScrollState.value}")
                        println("X drag = ${dragAmount.x.toInt()}")
                        println("X state = ${horizontalScrollState.value}")

                        verticalScrollState.scrollTo(verticalScrollState.value - dragAmount.y.toInt())

                        horizontalScrollState.scrollTo(horizontalScrollState.value - dragAmount.x.toInt())
                    }
                }
            }
            .fillMaxHeight()
            .background(Color.White)
            .drawBehind {
                drawLine(
                    color = borderColor,
                    strokeWidth = Dp(2f).toPx(),
                    start = Offset(0f, 0f),
                    end = Offset(size.width, 0f)
                )
            },
        contentAlignment = Alignment.TopStart
    ) {

        Row {
            //Names
            Column(
                modifier = Modifier.verticalScroll(verticalScrollState)
                    .background(Color.Transparent)
                    .border(Dp(1f), borderColor)
            ) {
                Box(
                    modifier = Modifier.size(height = Dp(50f), width = Dp(10f))
                        .background(Color.Transparent)
                )
                repeat(listOfPlans.size) { index ->
                    if (index == 0) {
                        Spacer(modifier = Modifier.height(cellSpacingTop))
                    } else {
                        Spacer(modifier = Modifier.height(cellSpacing))
                    }
                    Box(
                        modifier = Modifier.height(cellHeight)
                            .width(
                                if (namesIconsShown || expandedNames.value) (Dp(150f)) else Dp(
                                    50f
                                )
                            )
                            .background(Color.Transparent), contentAlignment = Alignment.CenterStart
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.padding(start = Dp(10f)).clickable {
                                if (isMobile)
                                    expandedNames.value = !expandedNames.value
                            }) {
                            if (listOfPlans[index].person.isNotEmpty()) {
                                if (!isMobile) {
                                    CircleWithInitial(
                                        name = listOfPlans[index].person.uppercase(),
                                        modifier = Modifier.clickable {
                                            if (isMobile)
                                                expandedNames.value = true
                                        },
                                        fontSize = 12
                                    )
                                    if (namesIconsShown) {
                                        Spacer(Modifier.width(Dp(8f)))
                                        Text(
                                            listOfPlans[index].person,
                                            textAlign = TextAlign.Center,
                                            color = namesColor,
                                            maxLines = 1,
                                            fontSize = Sp(14f),
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                } else {
                                    if (expandedNames.value) {
                                        Text(
                                            text = listOfPlans[index].person,
                                            textAlign = TextAlign.Center,
                                            color = namesColor,
                                            modifier = Modifier.clickable {
                                                if (isMobile)
                                                    expandedNames.value = false
                                            },
                                            fontSize = Sp(14f),
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    } else {
                                        CircleWithInitial(
                                            name = listOfPlans[index].person.uppercase(),
                                            modifier = Modifier.clickable {
                                                if (isMobile)
                                                    expandedNames.value = true
                                            },
                                            fontSize = 12
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            val pathEffect =
                PathEffect.dashPathEffect(floatArrayOf(5f, 5f), phase = 0f)

            Column(
                modifier = Modifier
                    .background(Color.Transparent)
            ) {

                //Hours
                Row(
                    modifier = Modifier.height(cellHeight)
                        .horizontalScroll(horizontalScrollState),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    repeat(24 - startHour + 1) { index ->
                        Box(
                            modifier = Modifier
                                .width(Dp(celWidthDp.value.toFloat()))
                                .height(cellHeight)
                                .drawBehind {
                                    drawLine(
                                        color = borderColor,
                                        start = Offset(x = 0f, y = size.height),
                                        end = Offset(x = size.width, y = size.height),
                                        strokeWidth = Dp(1f).toPx()
                                    )
                                    drawLine(
                                        color = borderPathColor,
                                        start = Offset(x = size.width, y = 0f),
                                        end = Offset(x = size.width, y = size.height),
                                        pathEffect = pathEffect,
                                        strokeWidth = Dp(1f).toPx()
                                    )
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                "${index + startHour}:00",
                                textAlign = TextAlign.Center,
                                color = hoursColor,
                                fontSize = Sp(14f),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                }

                //content
                Column(
                    modifier = Modifier
                        .horizontalScroll(horizontalScrollState)
                        .verticalScroll(verticalScrollState)
                ) {
                    val textMeasurer = rememberTextMeasurer()
                    listOfPlans.forEachIndexed { rowIndex, plan ->
                        if (rowIndex == 0) {
                            Canvas(
                                modifier = Modifier.width(tableWidth.value).height(cellSpacingTop)
                            ) {
                                for (i in 0..hourRange) {
                                    if (i > 0)
                                        drawLine(
                                            borderPathColor,
                                            start = Offset(
                                                Dp(celWidthDp.value.toFloat()).toPx() * i,
                                                0f
                                            ),
                                            end = Offset(
                                                Dp(celWidthDp.value.toFloat()).toPx() * i,
                                                cellSpacingTop.toPx()
                                            ),
                                            pathEffect = pathEffect,
                                            strokeWidth = Dp(1f).toPx()
                                        )
                                }
                            }
                        } else {
                            Canvas(
                                modifier = Modifier.width(Dp(tableWidth.value.value))
                                    .height(cellSpacing)
                            ) {
                                for (i in 0..hourRange) {
                                    if (i > 0)
                                        drawLine(
                                            borderPathColor,
                                            start = Offset(
                                                Dp(celWidthDp.value.toFloat()).toPx() * i,
                                                0f
                                            ),
                                            end = Offset(
                                                Dp(celWidthDp.value.toFloat()).toPx() * i,
                                                cellSpacing.toPx()
                                            ),
                                            pathEffect = pathEffect,
                                            strokeWidth = Dp(1f).toPx()
                                        )
                                }
                            }
                        }
                        Canvas(
                            modifier = Modifier
                                .size(
                                    width = tableWidth.value,
                                    height = cellHeight
                                )
                                .pointerHoverIcon(PointerIcon.Hand)
                                .pointerInput(plan) {
                                    detectTapGestures { offset ->
                                        val rowHeightPx = cellHeight.toPx()
                                        val tableWidthPx = tableWidth.value.toPx()
                                        val startMinute = startHour * 60
                                        val totalMinutes = 1440 - startMinute

                                        val rowTop = 0f
                                        plan.events.forEach { event ->
                                            val fromMin =
                                                event.from.hour * 60 + event.from.minute
                                            val toMin =
                                                event.to?.let { it.hour * 60 + it.minute }
                                                    ?: 1440

                                            val x1 =
                                                ((fromMin - startMinute).coerceAtLeast(0) * tableWidthPx) / totalMinutes
                                            val x2 =
                                                ((toMin - startMinute).coerceAtLeast(0) * tableWidthPx) / totalMinutes

                                            val width = maxOf(0f, x2 - x1)
                                            val rectTopLeft = Offset(x1, rowTop)
                                            val rectSize = Size(width, rowHeightPx)

                                            val inBounds =
                                                offset.x in rectTopLeft.x..(rectTopLeft.x + rectSize.width) &&
                                                        offset.y in rectTopLeft.y..(rectTopLeft.y + rectSize.height)

                                            if (inBounds) {
                                                println("ScheduleDay onEventClicked ${event.title}")
                                                onEventClicked(event)
                                                return@detectTapGestures
                                            }
                                        }
                                    }
                                }
                        ) {

                            //lines
                            for (i in 0..hourRange) {
                                if (i > 0)
                                    drawLine(
                                        borderPathColor,
                                        start = Offset(
                                            Dp(celWidthDp.value.toFloat()).toPx() * i,
                                            0f
                                        ),
                                        end = Offset(
                                            Dp(celWidthDp.value.toFloat()).toPx() * i,
                                            cellHeight.toPx()
                                        ),
                                        pathEffect = pathEffect,
                                        strokeWidth = Dp(1f).toPx()
                                    )
                            }

                            //plans
                            plan.events.forEach { event ->
                                val minX1 = event.from.hour * 60 + event.from.minute
                                val x1 = maxOf(
                                    0f,
                                    (tableWidth.value.toPx() * (minX1 - (startHour * 60))) / (1440 - (startHour * 60))
                                )

                                val minX2 =
                                    event.to?.let { to -> to.hour * 60 + to.minute }
                                        ?: (24 * 60)
                                val x2 = maxOf(
                                    0f,
                                    (tableWidth.value.toPx() * (minX2 - (startHour * 60))) / (1440 - (startHour * 60))
                                )

                                val width = maxOf(0f, x2 - x1)
                                val topLeft = Offset(x1, 0f)
                                val size = Size(width, cellHeight.toPx())

                                drawRoundRect(
                                    color = event.color,
                                    topLeft = topLeft,
                                    size = size,
                                    cornerRadius = CornerRadius(10f, 10f)
                                )
                                val measuredText =
                                    textMeasurer.measure(
                                        AnnotatedString(event.title ?: ""),
                                        constraints = Constraints.fixedWidth(size.width.toInt()),
                                        overflow = TextOverflow.Ellipsis,
                                        style = TextStyle(
                                            fontSize = Sp(16f),
                                            color = Color.Black,
                                            textAlign = TextAlign.Center
                                        ),
                                        maxLines = 1
                                    )
                                val textOffsetX =
                                    topLeft.x + (size.width - measuredText.size.width) / 2
                                val textOffsetY =
                                    topLeft.y + cellHeight.toPx() / 2 - measuredText.size.height / 2
                                drawText(
                                    measuredText,
                                    topLeft = Offset(
                                        textOffsetX,
                                        textOffsetY
                                    )
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}